"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { CATALOG, type Product } from "@/lib/catalog";

export default function CatalogPage() {
  const [products] = useState<Product[]>(CATALOG);
  const [filter, setFilter] = useState<string>("All");
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const categories = ["All", ...Array.from(new Set(CATALOG.map((p) => p.category)))];
  const filtered = filter === "All" ? products : products.filter((p) => p.category === filter);

  if (!mounted) return null;

  return (
    <div className="flex flex-col flex-1">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0F0F0F]/80 border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-tight">
              Dressing<span className="text-[#FF6B35]">Room</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link href="/catalog" className="text-sm text-white font-medium">
              Catalog
            </Link>
            <Link href="/profile" className="text-sm text-gray-400 hover:text-white transition-colors">
              My Profile
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12 w-full">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Catalog</h1>
        <p className="text-gray-400 mb-8">Pick an outfit to try on virtually.</p>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
                filter === cat
                  ? "bg-[#FF6B35] text-white"
                  : "bg-[#1A1A1A] text-gray-400 border border-[#2A2A2A] hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((product) => (
            <Link
              key={product.id}
              href={`/try-on/${product.id}`}
              className="group rounded-2xl bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden hover:border-[#FF6B35]/40 transition-all hover:-translate-y-1"
            >
              {/* Image placeholder */}
              <div className="aspect-[3/4] bg-gradient-to-br from-[#222] to-[#1A1A1A] flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[#FF6B35]/0 group-hover:bg-[#FF6B35]/5 transition-colors" />
                <div className="text-center px-4">
                  <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-[#2A2A2A] flex items-center justify-center text-3xl">
                    {product.category === "Dresses" ? "👗" :
                     product.category === "Pants" ? "👖" :
                     product.category === "Jackets" ? "🧥" :
                     product.category === "Hoodies" ? "👕" :
                     product.category === "Activewear" ? "🏃" :
                     "👕"}
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{product.category}</p>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500">{product.brand}</p>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#FF6B35]">★</span>
                    <span className="text-xs text-gray-400">{product.rating}</span>
                  </div>
                </div>
                <h3 className="text-sm font-semibold mb-2 tracking-tight">{product.name}</h3>

                {/* Color swatches */}
                <div className="flex items-center gap-1.5 mb-3">
                  {product.colors.map((c) => (
                    <div
                      key={c.hex}
                      className="w-4 h-4 rounded-full border border-[#333]"
                      style={{ backgroundColor: c.hex }}
                      title={c.name}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">${product.price.toFixed(0)}</span>
                  <span className="text-xs text-gray-500">{product.reviews} reviews</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
